import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { graphqlOperation, API } from 'aws-amplify';
import { onCreateNotification } from '../../../../graphql/subscriptions';
import GraphQLAPI from '../../../../api-graphql';
import { connect } from 'react-redux';
import { IconButton, Badge, Popover, Link, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { NotificationsOutlined } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { getIdentities, updateListDocuments, updateListBundles } from '../../../../actions';
import NotificationList from './NotificationList';

// Notification Items
const useStyles = makeStyles((theme) => ({
  listItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.background.default
    }
  },
  bell: {
    color: theme.palette.primary.main,
    position: 'relative'
  },
  iconButton: {
    [theme.breakpoints.down(990)]: {
      marginRight: '10px',
      '& span span svg': {
        fontSize: '1.3rem'
      }
    },
    position: 'relative'
  },
  red: {
    backgroundColor: 'red'
  },
  yellow: {
    backgroundColor: 'yellow'
  },
  green: {
    backgroundColor: 'green'
  } }));

const adapterNotification = (data) => {
  const { flow, document } = data;
  return {
    flow: flow.flowStringfy,
    flowId: flow.flowId,
    status: document.status
  };
};

const NotificationsClass = (props) => {
  const { updateListDocumentsDispatch, updateListBundleDispatch, getAllIdentities, subgroup, group } = props;
  const [notificationsList, setNotificationsList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useStyles();
  const { t } = useTranslation();

  let subscription;

  useEffect(() => {
    async function getInitialData () {
      const notifications = await GraphQLAPI.listNotifications({ read: { eq: 'false' } }, subgroup)
        .catch((err) => console.error('Error while fetching notifications ', err));
      setNotificationsList(notifications.data.listNotifications.items);

      subscription = await API.graphql(
        graphqlOperation(onCreateNotification, { group, subgroups: subgroup })
      ).subscribe({
        next: (data) => {
          switch (data.value.data.onCreateNotification.type) {
            case 'identity':
              getAllIdentities();
              break;
            case 'document':
              updateListDocumentsDispatch({ newItemUpdated: adapterNotification(JSON.parse(data.value.data.onCreateNotification.data)) });
              break;
            case 'bundle':
              updateListBundleDispatch({ newItemUpdated: JSON.parse(data.value.data.onCreateNotification.data) });
              break;
            default:
              break;
          }
          if (data.value.data.onCreateNotification.visible) {
            setNotificationsList(s => [data.value.data.onCreateNotification].concat(s));
          }
        },
        error: error => {
          console.warn(error);
          getInitialData();
        }
      });
      return subscription;
    }
    getInitialData();

    return () => {
      // unmount
      try {
        subscription.unsubscribe();
      } catch (err) {
        console.log('error unsubscribe notifications', err);
      }
    };
  }, [subgroup]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markAsRead = (id) => {
    let input = {
      id,
      read: true
    };
    GraphQLAPI.updateNotifications(input)
      .then(() => {
        setNotificationsList(s => s.filter((n) => n.id !== id));
      })
      .catch((err) => console.error('Error while updating notification ', err));
  };

  const markAllAsRead = () => {
    notificationsList.forEach(notification => markAsRead(notification.id));
  };

  return (
    <div>
      <Tooltip title={t('toolbar:notifications:tooltip')}>
        <IconButton
          color='primary'
          aria-owns={anchorEl ? 'notification-list' : undefined}
          aria-haspopup='true'
          onClick={handleOpen}
          fontSize='large'
          className={classes.iconButton}
        >
          <Badge
            badgeContent={notificationsList.length}
            className={classes.bell}
            color='primary'
          >
            <NotificationsOutlined className={classes.bell} fontSize='large' color='primary' />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        id='notification-list'
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        disableScrollLock
        style={{ maxHeight: 450 }}
      >
        {notificationsList.length ? (
          <>
            <div
              style={{ padding: 10, textAlign: 'right' }}
              onClick={markAllAsRead}
            >
              <Link href='#' variant='caption'>
                {t('notification:markAllAsRead')}
              </Link>
            </div>
            <NotificationList markAsRead={markAsRead} notificationsList={notificationsList} />
          </>
        ) : (
          <div
            style={{ padding: 16, textAlign: 'center' }}
            onClick={handleClose}
          >
            {t('notification:noNotification')}
          </div>
        )}
      </Popover>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    filter: state.filter,
    visibleTabs: state.config.visibleTabs,
    documents: state.documents.documents,
    subgroup: state.config.subgroup,
    group: state.config.group
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getAllIdentities: (...args) =>
      dispatch(getIdentities(...args)),
    updateListBundleDispatch: (...args) =>
      dispatch(updateListBundles(...args)),
    updateListDocumentsDispatch: (...args) =>
      dispatch(updateListDocuments(...args))
  };
};

NotificationsClass.propTypes = {
  updateListBundleDispatch: PropTypes.func.isRequired,
  updateListDocumentsDispatch: PropTypes.func.isRequired,
  getAllIdentities: PropTypes.func.isRequired,
  getBundles: PropTypes.func.isRequired,
  subgroup: PropTypes.string.isRequired,
  group: PropTypes.string.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(memo(NotificationsClass));
